import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { HardHat, Package, AlertTriangle, CheckCircle, Clock, Calendar, User as UserIcon, MapPin, Check, Plus, FileText, Camera, X, Edit, Search, Shield } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const PPE = ({ openAddModal = false }) => {
  const { user } = useContext(UserContext);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(openAddModal);

  // Form data for personal equipment
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [viNumber, setViNumber] = useState('');
  const [size, setSize] = useState('');
  const [manufactureDate, setManufactureDate] = useState('');
  const [comment, setComment] = useState('');
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Historical inspection data
  const [addHistoricalInspection, setAddHistoricalInspection] = useState(false);
  const [inspectionData, setInspectionData] = useState({
    inspection_date: '',
    inspector: '',
    validity_date: '',
    status: 'ok',
    comments: '',
    external_company_name: '',
    inspection_file: null
  });

  // Sharing modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showStopShareModal, setShowStopShareModal] = useState(false);
  const [sharingEquipment, setSharingEquipment] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [sharingSubmitting, setSharingSubmitting] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [editFormData, setEditFormData] = useState({
    safety_equipment_serial_number: '',
    safety_equipment_vi_number: '',
    safety_equipment_size: '',
    safety_equipment_manufacture_date: '',
    safety_equipment_comment: ''
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    fetchEquipment();
  }, []);

  useEffect(() => {
    if (showAddModal) {
      fetchEquipmentTypes();
    }
  }, [showAddModal]);

  // Fetch models when type changes
  useEffect(() => {
    if (selectedType) {
      fetchEquipmentModels(selectedType);
    } else {
      setEquipmentModels([]);
      setSelectedModel('');
    }
  }, [selectedType]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Use personal-equipment endpoint to get only user's private equipment
      const response = await axios.get(`${API_BASE_URL}/api/personal-equipment/`, { headers });

      const equipmentData = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.results) ? response.data.results : []);

      setEquipment(equipmentData);
    } catch (error) {
      console.error('Error fetching equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch equipment types - only GLOBAL types for personal equipment
  const fetchEquipmentTypes = async () => {
    try {
      setLoadingTypes(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // scope=all_global returns only global types (company=null)
      const response = await axios.get(`${API_BASE_URL}/api/safetyequipmenttype/?scope=all_global`, { headers });

      const typesData = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.results) ? response.data.results : []);

      setEquipmentTypes(typesData);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  // Fetch equipment models based on type - only GLOBAL models for personal equipment
  const fetchEquipmentModels = async (typeId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // scope=global returns only global models (company=null)
      const response = await axios.get(`${API_BASE_URL}/api/safetyequipmentmodel/?scope=global`, { headers });

      const allModels = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.results) ? response.data.results : []);

      // Filter models for the selected global type
      const modelsForType = allModels.filter(model =>
        model.safety_equipment_type === parseInt(typeId)
      );

      setEquipmentModels(modelsForType);
    } catch (error) {
      console.error('Error fetching equipment models:', error);
    }
  };

  // Reset form helper
  const resetForm = () => {
    setSelectedType('');
    setSelectedModel('');
    setSerialNumber('');
    setViNumber('');
    setSize('');
    setManufactureDate('');
    setComment('');
    setEquipmentModels([]);
    setAddHistoricalInspection(false);
    setInspectionData({
      inspection_date: '',
      inspector: '',
      validity_date: '',
      status: 'ok',
      comments: '',
      external_company_name: '',
      inspection_file: null
    });
  };

  // Handle inspection input change
  const handleInspectionInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'inspection_file') {
      setInspectionData(prev => ({
        ...prev,
        inspection_file: files && files.length > 0 ? files[0] : null
      }));
    } else if (name === 'status') {
      // When status is set to 'non-ok', auto-set validity date to today
      if (value === 'non-ok') {
        const today = new Date().toISOString().split('T')[0];
        setInspectionData(prev => ({
          ...prev,
          status: value,
          validity_date: today
        }));
      } else {
        setInspectionData(prev => ({
          ...prev,
          status: value
        }));
      }
    } else {
      setInspectionData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Create inspection for equipment
  const createInspectionForEquipment = async (equipmentId, token) => {
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('inspection_date', inspectionData.inspection_date);
    formDataToSubmit.append('inspector', inspectionData.inspector);
    formDataToSubmit.append('validity_date', inspectionData.validity_date);
    formDataToSubmit.append('status', inspectionData.status);
    formDataToSubmit.append('comments', inspectionData.comments || '');
    formDataToSubmit.append('safety_equipment', equipmentId);
    formDataToSubmit.append('inspection_type', 'external'); // Always external for user-added inspections
    formDataToSubmit.append('external_company_name', inspectionData.external_company_name);

    if (inspectionData.inspection_file) {
      formDataToSubmit.append('inspection_file', inspectionData.inspection_file);
    }

    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    };

    await axios.post(`${API_BASE_URL}/api/inspection/`, formDataToSubmit, config);
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Add new personal equipment
  const handleAddEquipment = async () => {
    if (!selectedType || !serialNumber) {
      alert('Please fill in all required fields (Equipment Type, Serial Number).');
      return;
    }

    // Validate manufacture date - cannot be in the future
    if (manufactureDate) {
      const manufactureDateObj = new Date(manufactureDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (manufactureDateObj > today) {
        alert('Manufacture Date cannot be in the future.');
        return;
      }
    }

    // Validate inspection data if adding historical inspection
    if (addHistoricalInspection) {
      if (!inspectionData.inspection_date || !inspectionData.inspector ||
          !inspectionData.validity_date || !inspectionData.external_company_name) {
        alert('Please fill in all required inspection fields (Inspection Date, Inspector, Validity Date, External Company).');
        return;
      }

      // Validate inspection date - cannot be in the future (historical inspection)
      const inspectionDateObj = new Date(inspectionData.inspection_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (inspectionDateObj > today) {
        alert('Inspection Date cannot be in the future for historical inspections.');
        return;
      }
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('access_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Personal equipment data - backend will set personal_owner, company=null, status='private'
      const equipmentData = {
        safety_equipment_type: parseInt(selectedType),
        safety_equipment_model: selectedModel ? parseInt(selectedModel) : null,
        safety_equipment_serial_number: serialNumber,
        safety_equipment_vi_number: viNumber || null,
        safety_equipment_size: size || null,
        safety_equipment_manufacture_date: manufactureDate || null,
        safety_equipment_comment: comment || null,
      };

      // Use personal-equipment endpoint which handles personal equipment logic
      const response = await axios.post(`${API_BASE_URL}/api/personal-equipment/`, equipmentData, { headers });
      const createdEquipment = response.data;

      // If historical inspection should be added, create it
      if (addHistoricalInspection && createdEquipment.id) {
        try {
          await createInspectionForEquipment(createdEquipment.id, token);
        } catch (inspectionError) {
          console.error('Error creating inspection:', inspectionError);
          alert('Equipment created but failed to add inspection. You can add it later.');
        }
      }

      alert('Personal equipment added successfully!');
      setShowAddModal(false);
      resetForm();
      // Refresh equipment list
      fetchEquipment();
    } catch (error) {
      console.error('Error adding equipment:', error);
      const errorMessage = error.response?.data
        ? JSON.stringify(error.response.data)
        : 'Unknown error';
      alert(`Failed to add equipment: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm receipt of equipment
  const confirmReceipt = async (equipmentId) => {
    if (!window.confirm('Confirm that you have received this equipment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

      await axios.patch(
        `${API_BASE_URL}/api/safetyequipment/${equipmentId}/`,
        { safety_equipment_status: 'received' },
        { headers }
      );

      alert('Equipment receipt confirmed successfully!');
      fetchEquipment();
    } catch (error) {
      console.error('Error confirming receipt:', error);
      alert('Failed to confirm equipment receipt. Please try again.');
    }
  };

  // Fetch available companies for sharing
  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/company/`, { headers });

      const companiesData = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.results) ? response.data.results : []);

      setAvailableCompanies(companiesData);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setAvailableCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Open make visible modal
  const openMakeVisibleModal = (item) => {
    setSharingEquipment(item);
    setSelectedCompanies([]);
    setShowShareModal(true);
    fetchCompanies();
  };

  // Open hide modal
  const openHideModal = (item) => {
    setSharingEquipment(item);
    // Pre-select all currently visible companies for removal
    const currentVisibleIds = item.shared_with_companies || [];
    setSelectedCompanies(currentVisibleIds);
    setShowStopShareModal(true);
  };

  // Handle company selection toggle
  const toggleCompanySelection = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  // Submit make visible - add companies to visibility list
  const handleMakeVisible = async () => {
    if (selectedCompanies.length === 0) {
      alert('Please select at least one company.');
      return;
    }

    try {
      setSharingSubmitting(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

      // Get current visible companies and add new ones
      const currentVisibleIds = sharingEquipment.shared_with_companies || [];
      const newVisibleIds = [...new Set([...currentVisibleIds, ...selectedCompanies])];

      await axios.patch(
        `${API_BASE_URL}/api/personal-equipment/${sharingEquipment.id}/`,
        {
          is_shared: true,
          shared_with_companies: newVisibleIds
        },
        { headers }
      );

      alert('Equipment is now visible to selected companies.');
      setShowShareModal(false);
      setSharingEquipment(null);
      setSelectedCompanies([]);
      fetchEquipment();
    } catch (error) {
      console.error('Error making equipment visible:', error);
      alert('Failed to make equipment visible. Please try again.');
    } finally {
      setSharingSubmitting(false);
    }
  };

  // Submit hide - remove companies from visibility list
  const handleHide = async () => {
    if (selectedCompanies.length === 0) {
      alert('Please select at least one company to hide from.');
      return;
    }

    try {
      setSharingSubmitting(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

      // Remove selected companies from visible list
      const currentVisibleIds = sharingEquipment.shared_with_companies || [];
      const newVisibleIds = currentVisibleIds.filter(id => !selectedCompanies.includes(id));

      // If no companies left, set is_shared to false
      const isStillVisible = newVisibleIds.length > 0;

      await axios.patch(
        `${API_BASE_URL}/api/personal-equipment/${sharingEquipment.id}/`,
        {
          is_shared: isStillVisible,
          shared_with_companies: newVisibleIds
        },
        { headers }
      );

      alert('Equipment is now hidden from selected companies.');
      setShowStopShareModal(false);
      setSharingEquipment(null);
      setSelectedCompanies([]);
      fetchEquipment();
    } catch (error) {
      console.error('Error hiding equipment:', error);
      alert('Failed to hide equipment. Please try again.');
    } finally {
      setSharingSubmitting(false);
    }
  };

  // Hide from all companies
  const hideFromAll = async (item) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

      await axios.patch(
        `${API_BASE_URL}/api/personal-equipment/${item.id}/`,
        {
          is_shared: false,
          shared_with_companies: []
        },
        { headers }
      );

      alert('Equipment is now hidden from companies.');
      fetchEquipment();
    } catch (error) {
      console.error('Error hiding equipment:', error);
      alert('Failed to hide equipment. Please try again.');
    }
  };

  // Open edit modal
  const openEditModal = (item) => {
    setEditingEquipment(item);
    setEditFormData({
      safety_equipment_serial_number: item.safety_equipment_serial_number || '',
      safety_equipment_vi_number: item.safety_equipment_vi_number || '',
      safety_equipment_size: item.safety_equipment_size || '',
      safety_equipment_manufacture_date: item.safety_equipment_manufacture_date || '',
      safety_equipment_comment: item.safety_equipment_comment || ''
    });
    setShowEditModal(true);
  };

  // Handle edit form input change
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Update equipment
  const handleUpdateEquipment = async () => {
    if (!editFormData.safety_equipment_serial_number) {
      alert('Serial Number is required.');
      return;
    }

    // Validate manufacture date - cannot be in the future
    if (editFormData.safety_equipment_manufacture_date) {
      const manufactureDateObj = new Date(editFormData.safety_equipment_manufacture_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (manufactureDateObj > today) {
        alert('Manufacture Date cannot be in the future.');
        return;
      }
    }

    try {
      setEditSubmitting(true);
      const token = localStorage.getItem('access_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const updateData = {
        safety_equipment_serial_number: editFormData.safety_equipment_serial_number,
        safety_equipment_vi_number: editFormData.safety_equipment_vi_number || null,
        safety_equipment_size: editFormData.safety_equipment_size || null,
        safety_equipment_manufacture_date: editFormData.safety_equipment_manufacture_date || null,
        safety_equipment_comment: editFormData.safety_equipment_comment || null
      };

      await axios.patch(
        `${API_BASE_URL}/api/personal-equipment/${editingEquipment.id}/`,
        updateData,
        { headers }
      );

      alert('Equipment updated successfully!');
      setShowEditModal(false);
      setEditingEquipment(null);
      fetchEquipment();
    } catch (error) {
      console.error('Error updating equipment:', error);
      const errorMessage = error.response?.data
        ? JSON.stringify(error.response.data)
        : 'Unknown error';
      alert(`Failed to update equipment: ${errorMessage}`);
    } finally {
      setEditSubmitting(false);
    }
  };

  // Get equipment name
  const getEquipmentName = (item) => {
    const typeName = item.safety_equipment_type_name || '';
    const brandName = item.safety_equipment_model_brand || '';
    const modelName = item.safety_equipment_model_name || '';

    if (typeName && brandName) {
      return `${typeName} (${brandName}${modelName ? ' ' + modelName : ''})`;
    } else if (typeName) {
      return typeName;
    }

    return 'Unknown Equipment';
  };

  // Get equipment image URL
  const getEquipmentImageUrl = (item) => {
    const imageField = item.safety_equipment_model_image ||
                      item.safety_equipment_picture ||
                      item.image;

    if (!imageField) return null;

    if (imageField.startsWith('http')) {
      return imageField;
    }

    return `${API_BASE_URL}${imageField.startsWith('/') ? '' : '/'}${imageField}`;
  };

  // Get next inspection date - ONLY from actual inspection, not manufacturer validity
  const getNextInspectionDate = (item) => {
    // Only return date if there was an actual inspection
    return item.latest_inspection_validity_date || null;
  };

  // Get last inspection date
  const getLastInspectionDate = (item) => {
    return item.latest_inspection_date ||
           item.safety_equipment_date_of_inspection ||
           null;
  };

  // Check if equipment is expiring soon (within 30 days)
  const isExpiringSoon = (item) => {
    const nextInspectionDate = getNextInspectionDate(item);
    if (!nextInspectionDate) return false;

    const inspectionDate = new Date(nextInspectionDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    return inspectionDate > now && inspectionDate <= thirtyDaysFromNow;
  };

  // Check if equipment is expired
  const isExpired = (item) => {
    const nextInspectionDate = getNextInspectionDate(item);
    if (!nextInspectionDate) return false;

    const inspectionDate = new Date(nextInspectionDate);
    const now = new Date();

    return inspectionDate < now;
  };

  // Get status badge
  const getStatusBadge = (item) => {
    const status = item.safety_equipment_status?.toLowerCase() || 'unknown';

    if (isExpired(item)) {
      return (
        <span className="status-badge status-expired">
          <AlertTriangle size={12} />
          Expired
        </span>
      );
    }

    if (isExpiringSoon(item)) {
      return (
        <span className="status-badge status-expiring-soon">
          <Clock size={12} />
          Expiring Soon
        </span>
      );
    }

    if (status === 'private') {
      return (
        <span className="status-badge status-active">
          <CheckCircle size={12} />
          Active
        </span>
      );
    }

    return (
      <span className="status-badge status-unknown">
        {status}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-900">Personal Protective Equipment</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm hover:bg-brand-700 transition-colors duration-300 flex items-center shadow-lg shadow-brand-500/20"
          >
            <Plus size={16} className="mr-2" />
            Add Equipment
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 pb-4 overflow-hidden">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-steel-100 h-full flex flex-col">
          {/* Equipment Table */}
          {equipment.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <HardHat className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">No personal equipment found.</p>
                <p className="text-slate-400 text-sm mt-1">Click "Add Equipment" to add your PPE.</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <table className="w-full table-fixed divide-y divide-steel-200">
                  <thead className="bg-steel-100 border-b-2 border-brand-500 sticky top-0 z-10">
                    <tr>
                      <th className="w-[50px] px-2 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Image</th>
                      <th className="w-[25%] px-2 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Equipment</th>
                      <th className="w-[12%] px-2 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Mgf/Exp</th>
                      <th className="w-[12%] px-2 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Inspection</th>
                      <th className="w-[12%] px-2 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Assigned</th>
                      <th className="w-[15%] px-2 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Company</th>
                      <th className="w-[8%] px-2 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                      <th className="w-[80px] px-2 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-200">
                    {equipment.map((item) => {
                      const imageUrl = getEquipmentImageUrl(item);
                      const nextInspection = getNextInspectionDate(item);
                      const equipmentStatus = item.safety_equipment_status;

                      return (
                        <tr key={item.id} className="cursor-pointer hover:bg-brand-50 transition-colors">
                          {/* Image */}
                          <td className="px-2 py-2">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={getEquipmentName(item)}
                                className="w-8 h-8 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <HardHat className="w-8 h-8 text-slate-400" />
                            )}
                          </td>

                          {/* Equipment Name */}
                          <td className="px-2 py-2">
                            <div className="truncate">
                              <p className="font-medium text-slate-900 truncate">
                                {getEquipmentName(item)}
                              </p>
                              <p className="text-xs text-slate-500 truncate">
                                {(item.safety_equipment_serial_number) || 'N/N'}
                              </p>
                            </div>
                          </td>

                          {/* Mgf date/Validity */}
                          <td className="px-2 py-2 text-center">
                            <div className="flex flex-col gap-0.5">
                              {item.safety_equipment_manufacture_date ? (
                                <span className="text-xs">{formatDate(item.safety_equipment_manufacture_date)}</span>
                              ) : (
                                <span className="text-slate-400 text-xs">-</span>
                              )}
                              {item.manufacturer_validity_date ? (
                                <span className="text-[10px] text-slate-400">{formatDate(item.manufacturer_validity_date)}</span>
                              ) : null}
                            </div>
                          </td>

                          {/* Next Inspection */}
                          <td className="px-2 py-2 text-center">
                            {nextInspection ? (
                              <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                                isExpired(item)
                                  ? 'bg-red-50 text-red-700'
                                  : isExpiringSoon(item)
                                    ? 'bg-amber-50 text-amber-700'
                                    : 'bg-green-50 text-green-700'
                              }`}>
                                {formatDate(nextInspection)}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>

                          {/* Assigned By */}
                          <td className="px-2 py-2 text-center">
                            {item.assigned_by_name ? (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-xs truncate">
                                  {equipmentStatus === 'private' ? 'Own' : item.assigned_by_name}
                                </span>
                                {item.assigned_date && (
                                  <span className="text-[10px] text-slate-400">
                                    {formatDate(item.assigned_date.split('T')[0])}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>

                          {/* Company */}
                          <td className="px-2 py-2 text-center">
                            {equipmentStatus === 'private' ? (
                              item.is_shared && item.shared_with_company_names && item.shared_with_company_names.length > 0 ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-[10px] text-slate-400">Visible:</span>
                                  <span className="text-xs truncate">{item.shared_with_company_names.join(', ')}</span>
                                </div>
                              ) : (
                                <span className="text-slate-400 text-xs">-</span>
                              )
                            ) : (
                              item.company_name ? (
                                <span className="text-xs truncate">{item.company_name}</span>
                              ) : (
                                <span className="text-slate-400 text-xs">-</span>
                              )
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-2 py-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded-lg text-[10px] font-medium ${
                              equipmentStatus === 'private'
                                ? 'bg-purple-100 text-purple-800'
                                : equipmentStatus === 'issued'
                                  ? 'bg-amber-100 text-amber-800'
                                  : equipmentStatus === 'received'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-steel-100 text-slate-800'
                            }`}>
                              {equipmentStatus === 'private' ? 'Private' :
                               equipmentStatus === 'issued' ? 'Issued' :
                               equipmentStatus === 'received' ? 'Received' :
                               equipmentStatus}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-2 py-2 text-center">
                            <div className="flex justify-center gap-1">
                              {equipmentStatus === 'issued' ? (
                                <button
                                  onClick={() => confirmReceipt(item.id)}
                                  className="text-green-600 hover:text-green-700 p-1"
                                  title="Confirm receipt"
                                >
                                  <Check size={14} />
                                </button>
                              ) : equipmentStatus === 'private' ? (
                                <>
                                  <button
                                    onClick={() => openEditModal(item)}
                                    className="text-yellow-600 hover:text-yellow-700 p-1"
                                    title="Edit"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    onClick={() => openMakeVisibleModal(item)}
                                    className="text-blue-600 hover:text-blue-700 p-1"
                                    title="Visible"
                                  >
                                    <CheckCircle size={14} />
                                  </button>
                                  {item.is_shared && (
                                    <button
                                      onClick={() => openHideModal(item)}
                                      className="text-red-600 hover:text-red-700 p-1"
                                      title="Hide"
                                    >
                                      <X size={14} />
                                    </button>
                                  )}
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Equipment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
            addHistoricalInspection ? 'max-w-5xl' : 'max-w-xl'
          }`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-brand-50 to-white px-6 py-4 border-b border-brand-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center">
                  <Plus className="h-5 w-5 text-brand-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Add Personal Equipment</h2>
                  {addHistoricalInspection && (
                    <span className="text-xs text-emerald-600 font-medium">+ Inspection Data</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={submitting}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            {addHistoricalInspection ? (
              /* Two Panel Layout */
              <div className="flex-1 overflow-hidden flex">
                {/* Left Panel - Equipment Form */}
                <div className="w-1/2 border-r border-slate-200 flex flex-col">
                  <div className="p-6 flex-1 overflow-y-auto">
                    <div className="space-y-4">
                      {/* Equipment Type */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">
                          Equipment Type <span className="text-red-500">*</span>
                        </label>
                        {loadingTypes ? (
                          <div className="text-sm text-slate-500">Loading types...</div>
                        ) : (
                          <select
                            value={selectedType}
                            onChange={(e) => {
                              setSelectedType(e.target.value);
                              setSelectedModel('');
                            }}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          >
                            <option value="">Select equipment type</option>
                            {equipmentTypes.map((type) => (
                              <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Equipment Model */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">Equipment Model</label>
                        <select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          disabled={!selectedType}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-slate-100"
                        >
                          <option value="">Select equipment model (optional)</option>
                          {equipmentModels.map((model) => (
                            <option key={model.id} value={model.id}>{model.brand} {model.name}</option>
                          ))}
                        </select>
                        {!selectedType && <p className="text-xs text-slate-500 mt-1">Please select equipment type first</p>}
                        {selectedType && equipmentModels.length === 0 && <p className="text-xs text-slate-500 mt-1">No models available for this type</p>}
                      </div>

                      {/* Serial Number */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">
                          Serial Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={serialNumber}
                          onChange={(e) => setSerialNumber(e.target.value)}
                          placeholder="Enter serial number"
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>

                      {/* VI Number */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">VI Number</label>
                        <input
                          type="text"
                          value={viNumber}
                          onChange={(e) => setViNumber(e.target.value)}
                          placeholder="Enter VI number (optional)"
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>

                      {/* Size & Manufacture Date - same row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1.5">Size</label>
                          <input
                            type="text"
                            value={size}
                            onChange={(e) => setSize(e.target.value)}
                            placeholder="e.g. M, L, XL"
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1.5">Manufacture Date</label>
                          <input
                            type="date"
                            value={manufactureDate}
                            onChange={(e) => setManufactureDate(e.target.value)}
                            max={getTodayDate()}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>
                      </div>

                      {/* Comment */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">Comment</label>
                        <textarea
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Additional notes..."
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>

                      {/* Add Historical Inspection Checkbox */}
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="addHistoricalInspection"
                          checked={addHistoricalInspection}
                          onChange={(e) => setAddHistoricalInspection(e.target.checked)}
                          className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-slate-300 rounded"
                        />
                        <label htmlFor="addHistoricalInspection" className="text-sm font-medium text-slate-700">
                          Add historical inspection
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Panel - Inspection Form */}
                <div className="w-1/2 flex flex-col">
                  <div className="p-4 bg-emerald-50 border-b border-emerald-100">
                    <h3 className="text-sm font-bold text-emerald-900">Historical Inspection Data</h3>
                    <p className="text-xs text-emerald-700 mt-1">Add inspection data from your existing certificate</p>
                  </div>
                  <div className="p-6 flex-1 overflow-y-auto">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        {/* Inspection Date */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1.5">
                            Inspection Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="inspection_date"
                            value={inspectionData.inspection_date}
                            onChange={handleInspectionInputChange}
                            max={getTodayDate()}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>

                        {/* Inspector */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1.5">
                            Inspector <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="inspector"
                            value={inspectionData.inspector}
                            onChange={handleInspectionInputChange}
                            placeholder="Name of inspector"
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>

                        {/* Validity Date */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1.5">
                            Validity Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="validity_date"
                            value={inspectionData.validity_date}
                            onChange={handleInspectionInputChange}
                            disabled={inspectionData.status === 'non-ok'}
                            className={`w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                              inspectionData.status === 'non-ok' ? 'bg-slate-100' : ''
                            }`}
                          />
                        </div>

                        {/* Status */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1.5">
                            Status <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="status"
                            value={inspectionData.status}
                            onChange={handleInspectionInputChange}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          >
                            <option value="ok">OK</option>
                            <option value="non-ok">Non-OK</option>
                          </select>
                        </div>
                      </div>

                      {/* External Company Name */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">
                          Inspection Company <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="external_company_name"
                          value={inspectionData.external_company_name}
                          onChange={handleInspectionInputChange}
                          placeholder="Company that performed the inspection"
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>

                      {/* Comments */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">Comments</label>
                        <textarea
                          name="comments"
                          value={inspectionData.comments}
                          onChange={handleInspectionInputChange}
                          rows={2}
                          placeholder="Additional notes..."
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>

                      {/* PDF Upload */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">
                          <FileText className="inline h-4 w-4 mr-1" />
                          Inspection Certificate (PDF)
                        </label>
                        <input
                          type="file"
                          name="inspection_file"
                          accept="application/pdf"
                          onChange={handleInspectionInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                        />
                        {inspectionData.inspection_file && (
                          <p className="mt-1 text-xs text-slate-600">Selected: {inspectionData.inspection_file.name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Single Panel Layout */
              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                  {/* Equipment Type */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      Equipment Type <span className="text-red-500">*</span>
                    </label>
                    {loadingTypes ? (
                      <div className="text-sm text-slate-500">Loading types...</div>
                    ) : (
                      <select
                        value={selectedType}
                        onChange={(e) => {
                          setSelectedType(e.target.value);
                          setSelectedModel('');
                        }}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      >
                        <option value="">Select equipment type</option>
                        {equipmentTypes.map((type) => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Equipment Model */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Equipment Model</label>
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      disabled={!selectedType}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-slate-100"
                    >
                      <option value="">Select equipment model (optional)</option>
                      {equipmentModels.map((model) => (
                        <option key={model.id} value={model.id}>{model.brand} {model.name}</option>
                      ))}
                    </select>
                    {!selectedType && <p className="text-xs text-slate-500 mt-1">Please select equipment type first</p>}
                    {selectedType && equipmentModels.length === 0 && <p className="text-xs text-slate-500 mt-1">No models available for this type</p>}
                  </div>

                  {/* Serial Number */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      Serial Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      placeholder="Enter serial number"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>

                  {/* VI Number */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">VI Number</label>
                    <input
                      type="text"
                      value={viNumber}
                      onChange={(e) => setViNumber(e.target.value)}
                      placeholder="Enter VI number (optional)"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>

                  {/* Size & Manufacture Date - same row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Size</label>
                      <input
                        type="text"
                        value={size}
                        onChange={(e) => setSize(e.target.value)}
                        placeholder="e.g. M, L, XL"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Manufacture Date</label>
                      <input
                        type="date"
                        value={manufactureDate}
                        onChange={(e) => setManufactureDate(e.target.value)}
                        max={getTodayDate()}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                  </div>

                  {/* Comment */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Comment</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Additional notes about your equipment..."
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>

                  {/* Add Historical Inspection Checkbox */}
                  <div className="flex items-center space-x-2 pt-2">
                    <input
                      type="checkbox"
                      id="addHistoricalInspection"
                      checked={addHistoricalInspection}
                      onChange={(e) => setAddHistoricalInspection(e.target.checked)}
                      className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-slate-300 rounded"
                    />
                    <label htmlFor="addHistoricalInspection" className="text-sm font-medium text-slate-700">
                      Add historical inspection
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">Add inspection data from a previous inspection certificate</p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleAddEquipment}
                className="px-4 py-2 text-sm font-bold text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Adding...' : (addHistoricalInspection ? 'Add Equipment with Inspection' : 'Add Equipment')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Make Visible Modal */}
      {showShareModal && sharingEquipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-white px-6 py-4 border-b border-blue-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">Make Equipment Visible</h2>
              </div>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setSharingEquipment(null);
                  setSelectedCompanies([]);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={sharingSubmitting}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-sm text-slate-600 mb-4">
                Select companies that can see <strong className="text-slate-900">{getEquipmentName(sharingEquipment)}</strong>:
              </p>

              {loadingCompanies ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto"></div>
                  <p className="text-sm text-slate-500 mt-3">Loading companies...</p>
                </div>
              ) : availableCompanies.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No companies available.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableCompanies.map((company) => {
                    const isAlreadyVisible = sharingEquipment.shared_with_companies?.includes(company.id);
                    return (
                      <label
                        key={company.id}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedCompanies.includes(company.id)
                            ? 'border-brand-500 bg-brand-50'
                            : isAlreadyVisible
                              ? 'border-emerald-300 bg-emerald-50 opacity-60'
                              : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCompanies.includes(company.id) || isAlreadyVisible}
                          onChange={() => !isAlreadyVisible && toggleCompanySelection(company.id)}
                          disabled={isAlreadyVisible}
                          className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-slate-300 rounded"
                        />
                        <span className={`ml-3 text-sm font-medium ${isAlreadyVisible ? 'text-slate-500' : 'text-slate-900'}`}>
                          {company.name}
                          {isAlreadyVisible && <span className="ml-2 text-xs text-emerald-600">(already visible)</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 gap-3">
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setSharingEquipment(null);
                  setSelectedCompanies([]);
                }}
                className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={sharingSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleMakeVisible}
                className="px-4 py-2 text-sm font-bold text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50"
                disabled={sharingSubmitting || selectedCompanies.length === 0}
              >
                {sharingSubmitting ? 'Saving...' : `Make visible to ${selectedCompanies.length} company${selectedCompanies.length !== 1 ? 'ies' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Equipment Modal */}
      {showEditModal && editingEquipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-50 to-white px-6 py-4 border-b border-amber-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Edit className="h-5 w-5 text-amber-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">Edit Personal Equipment</h2>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEquipment(null);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={editSubmitting}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Equipment info - read only */}
              <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Equipment</h3>
                <p className="text-lg font-bold text-slate-900">{getEquipmentName(editingEquipment)}</p>
              </div>

              <div className="space-y-4">
                {/* Serial Number */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Serial Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="safety_equipment_serial_number"
                    value={editFormData.safety_equipment_serial_number}
                    onChange={handleEditInputChange}
                    placeholder="Enter serial number"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>

                {/* VI Number */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">VI Number</label>
                  <input
                    type="text"
                    name="safety_equipment_vi_number"
                    value={editFormData.safety_equipment_vi_number}
                    onChange={handleEditInputChange}
                    placeholder="Enter VI number (optional)"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>

                {/* Size & Manufacture Date - same row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Size</label>
                    <input
                      type="text"
                      name="safety_equipment_size"
                      value={editFormData.safety_equipment_size}
                      onChange={handleEditInputChange}
                      placeholder="e.g. M, L, XL"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Manufacture Date</label>
                    <input
                      type="date"
                      name="safety_equipment_manufacture_date"
                      value={editFormData.safety_equipment_manufacture_date}
                      onChange={handleEditInputChange}
                      max={getTodayDate()}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Comment</label>
                  <textarea
                    name="safety_equipment_comment"
                    value={editFormData.safety_equipment_comment}
                    onChange={handleEditInputChange}
                    placeholder="Additional notes about your equipment..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEquipment(null);
                }}
                className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={editSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateEquipment}
                className="px-4 py-2 text-sm font-bold text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50"
                disabled={editSubmitting}
              >
                {editSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide Equipment Modal */}
      {showStopShareModal && sharingEquipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-50 to-white px-6 py-4 border-b border-red-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <X className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">Hide Equipment</h2>
              </div>
              <button
                onClick={() => {
                  setShowStopShareModal(false);
                  setSharingEquipment(null);
                  setSelectedCompanies([]);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={sharingSubmitting}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-sm text-slate-600 mb-4">
                Select companies to hide <strong className="text-slate-900">{getEquipmentName(sharingEquipment)}</strong> from:
              </p>

              {sharingEquipment.shared_with_company_names && sharingEquipment.shared_with_company_names.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {sharingEquipment.shared_with_company_names.map((companyName, index) => {
                    const companyId = sharingEquipment.shared_with_companies?.[index];
                    return (
                      <label
                        key={companyId || index}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedCompanies.includes(companyId)
                            ? 'border-red-500 bg-red-50'
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCompanies.includes(companyId)}
                          onChange={() => toggleCompanySelection(companyId)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-slate-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-slate-900">
                          {companyName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No companies to hide from.</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 gap-3">
              <button
                onClick={() => {
                  setShowStopShareModal(false);
                  setSharingEquipment(null);
                  setSelectedCompanies([]);
                }}
                className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={sharingSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleHide}
                className="px-4 py-2 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={sharingSubmitting || selectedCompanies.length === 0}
              >
                {sharingSubmitting ? 'Hiding...' : `Hide from ${selectedCompanies.length} company${selectedCompanies.length !== 1 ? 'ies' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PPE;
